'use client'

import { useState, useRef, useEffect } from 'react'

import { Button } from '@/components/ui/button'

interface CalendarDropdownProps {
  googleUrl: string
  outlookUrl: string
  icsData: string
  filename: string
  label?: string
  compact?: boolean
}

/**
 * "Add to Calendar" menu.
 *
 * Trigger  — canonical Button, Figma link button 2198:34729 size Mini
 *            (bare inline text, zero padding, Body/bodySmAlt 14/16, underlined at
 *            rest). The bespoke `text-[#000000]/60` + `btn-underline` treatments are
 *            gone: Figma models button hover as a discrete colour swap with no motion.
 * Menu     — Figma Menu 2198:36869 / Menu item 2198:36854: theme/background fill,
 *            1px theme/border, radius sm (2px), shadow/DEFAULT (= Tailwind shadow-md),
 *            p-1; items h 32, px 8 / py 6, radius sm, Body/bodySm 14/16 weight 300 in
 *            theme/foreground, hover fill theme/secondary #74cdde. Mirrors the
 *            equivalent classes in `src/components/ui/select.tsx`.
 */
const MENU_ITEM =
  'flex h-8 w-full items-center rounded-sm px-2 py-1.5 text-left text-body-sm font-light text-foreground transition-colors hover:bg-secondary'

export function CalendarDropdown({
  googleUrl,
  outlookUrl,
  icsData,
  filename,
  label,
  compact,
}: CalendarDropdownProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const downloadIcs = () => {
    const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
    setOpen(false)
  }

  return (
    <div ref={ref} className="relative inline-block">
      <Button
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        size="mini"
        variant="link"
      >
        {compact ? (
          <>
            {label}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect width="18" height="18" x="3" y="4" rx="2" />
              <path d="M8 2v4" />
              <path d="M16 2v4" />
              <path d="M3 10h18" />
              <path d="M12 14v4" />
              <path d="M10 16h4" />
            </svg>
          </>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M8 2v4" />
              <path d="M16 2v4" />
              <rect width="18" height="18" x="3" y="4" rx="2" />
              <path d="M3 10h18" />
            </svg>
            {label || 'Add to Calendar'}
          </>
        )}
      </Button>

      {open && (
        <div className="absolute top-full left-0 mt-1 min-w-[160px] rounded-sm border border-border bg-background p-1 shadow-md z-50">
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={MENU_ITEM}
            onClick={() => setOpen(false)}
          >
            Google Calendar
          </a>
          <a
            href={outlookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={MENU_ITEM}
            onClick={() => setOpen(false)}
          >
            Outlook
          </a>
          <button type="button" onClick={downloadIcs} className={`${MENU_ITEM} cursor-pointer`}>
            Apple Calendar
          </button>
        </div>
      )}
    </div>
  )
}
